import { Link } from "react-router-dom";

import { Bell, Key, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePendingLicenseRequests } from "@/features/licenses/hooks/useLicenses";
import { softwareTypeLabel } from "@/features/servers/schemas/servers";

interface LicenseRequestsBellProps {
  enabled?: boolean;
}

/**
 * Campana del header: badge de pending + preview de solicitudes.
 */
export function LicenseRequestsBell({ enabled = true }: LicenseRequestsBellProps) {
  const { data, count, isLoading } = usePendingLicenseRequests(enabled);
  const preview = data.slice(0, 5);

  if (!enabled) {
    return (
      <button
        type="button"
        className="p-2 text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-100 tap-target"
        aria-label="Notificaciones"
        disabled
      >
        <Bell className="h-5 w-5 lg:h-6 lg:w-6" />
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative p-2 text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-100 tap-target cursor-pointer"
          aria-label={
            count > 0
              ? `${count} solicitudes de licencia pendientes`
              : "Solicitudes de licencia"
          }
        >
          <Bell className="h-5 w-5 lg:h-6 lg:w-6" />
          {count > 0 && (
            <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-white">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Key className="h-4 w-4" aria-hidden />
          Solicitudes pendientes
          {isLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {preview.length === 0 ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            No hay solicitudes pendientes
          </div>
        ) : (
          preview.map((req) => (
            <DropdownMenuItem key={req.id} asChild className="cursor-pointer">
              <Link to="/licenses" className="flex flex-col items-start gap-0.5">
                <span className="text-sm font-medium">
                  {softwareTypeLabel(req.softwareType)} · {req.clientName}
                </span>
                <span className="text-xs text-muted-foreground font-mono truncate max-w-full">
                  {req.machineId}
                </span>
              </Link>
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <div className="p-1">
          <Button
            variant="ghost"
            size="sm"
            className="w-full cursor-pointer"
            asChild
          >
            <Link to="/licenses">Ver todas</Link>
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
